/**
 * The TOUCH tier — phones and tablets together.
 *
 * ── Why this file exists, and why it is late ─────────────────────────
 *
 * `styles/responsive.css` has referred to it since TRBUD-254: *"`TOUCH_MAX_WIDTH`
 * in `apps/web/src/lib/useIsTouchLayout.ts` must carry the same number, and
 * `useIsTouchLayout.test.tsx` reads this file and asserts it."* Neither file
 * existed. So the 1099 in the stylesheet was guarded by a promise rather than by
 * a test, which is the exact drift `useIsCompact` was built to prevent one
 * breakpoint earlier — and the comment's confidence made it look handled.
 *
 * ── Touch is not the same question as compact ────────────────────────
 *
 * `useIsCompact` (699) asks "is this a phone-shaped viewport", and it drives
 * things that only make sense on a phone: the bottom tab bar, the compact
 * header, the single-column dialogs.
 *
 * This asks something different: "is a FINGER the pointer here". An iPad at 768
 * or 1024 CSS px answers no to the first and yes to the second, and that gap is
 * where the defect lived — those widths got the desktop table, whose row actions
 * sit behind a right-click a finger cannot perform.
 *
 * Both are width questions rather than device questions, deliberately, and for
 * the reason `useIsCompact` records: a desktop window dragged narrow gets the
 * touch layout, which is correct, because the layout being asked about is the one
 * the stylesheet has already applied. The separate `(pointer: coarse)` block in
 * the stylesheet is what handles the orthogonal question of target SIZE, and it
 * applies at any width.
 */
import { useMediaQuery } from "./useMediaQuery";

/**
 * The last width, in CSS pixels, that gets the touch layout.
 *
 * 1099 rather than a tablet-specific number because the tier runs all the way
 * down: a phone is a touch device too, and a range starting at 700 would leave
 * the phone tier out of every rule written against this constant.
 */
export const TOUCH_MAX_WIDTH = 1099;

/**
 * The query string, built from the constant above.
 *
 * `styles/responsive.css` must use the same figure, and nothing in CSS can
 * enforce that — so `useIsTouchLayout.test.tsx` opens the stylesheet and checks,
 * the same way `useIsCompact.test.tsx` does for 699.
 */
export const TOUCH_QUERY = `(max-width: ${TOUCH_MAX_WIDTH}px)`;

/**
 * True where the layout is the touch one — the widths at which `responsive.css`
 * shows `.queue-touch-list` and `.responsive-cards` and hides `.desktop-layout`.
 */
export function useIsTouchLayout(): boolean {
  return useMediaQuery(TOUCH_QUERY);
}
